import React, {useEffect, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useParams, useSearchParams} from "react-router";
import {toast} from "react-toastify";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import materialTransferService from "../../../../../services/web/material-transfer-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import useFetchOptions from "../../hooks/use-fetch-options.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import materialStorageService from "../../../../../services/web/material-storage-service.js";
import materialXStorageService from "../../../../../services/web/material-x-storage-service.js";
import materialService from "../../../../../services/web/material-service.js";
import ModalRequester from "./components/modal-requester.jsx";
import materialGroupService from "../../../../../services/web/material-group-service.js";
import Attach from "../../../../../components/attach/attach.jsx";
import Swal from "sweetalert2";

const initialData = {
    id: "",
    storage_id: "",
    storage_destination_id: "",
    requester_id: "",
    transfer_attach: "",
    group: "",
    material_x_storage_id: "",
    quantity: "",
}

let saveAdd = false, saveCopy = false;

const MaterialTransferForm = () => {
    const navigate = navigateHook();
    const [searchParams] = useSearchParams();
    const {id} = useParams()
    const [requesterName, setRequesterName] = useState("")
    const [data, setData] = useState(initialData)
    const [isLoading, setIsLoading] = useState(false)
    const [materialIsUnique, setMaterialIsUnique] = useState(false)
    const [modalRequester, setModalRequester] = useState(false)
    const [isCopying, setIsCopying] = useState(false)
    const [password, setPassword] = useState("")
    const {storageByUserOptions, storageOptions} = useFetchOptions({fetchStorageByUser: true, fetchStorage: true})

    const onChange = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))
    const onChangeSelect = (id, value) => setData(prev => ({...prev, [id]: value || ""}));

    const onChangeFile = (file, id) => {
        if (file) {
            setData(prev => ({ ...prev, [id]: file }));
        }
    }

    const removeFiel = (id) => {
        setData(prev => ({ ...prev, [id]: "" }));
    }

    useEffect(() => {
        const cpParam = searchParams.get("cp");
        if (cpParam) {
            materialTransferService.getById(cpParam)
                .then(async ({id, storage_id, storage_destination_id, requester_id, quantity, material_id}) => {
                    setIsCopying(true)
                    const {group_id} = await materialService.getById(material_id)
                    const materials = await materialXStorageService.optionsAvailableByGroupAndStorage(group_id, storage_id)
                    const materialXStorageId = materials.find(m => m.material_id === material_id)
                    setData({
                        id,
                        storage_id,
                        storage_destination_id,
                        requester_id,
                        group: group_id,
                        material_x_storage_id: materialXStorageId?.value,
                        quantity,
                    })
                }).finally(() => {
                    setTimeout(() => setIsCopying(false), 1000)
                })
        }
    }, []);

    const {data: transfer, refetch, status, isLoading: isLoadingData} = useQuery({
        queryKey: ['material-transfer'], queryFn: () => materialTransferService.getById(id), enabled: !!id
    });

    useEffect(() => {
        if (status === "success") {
            const transferNormalized = projectUtils.normalizeData(transfer)
            setData(transferNormalized)
        }
    }, [transfer, status]);

    const {data: groups, isLoading: isLoadingGroups} = useQuery({
        queryKey: ['material-groups', data.storage_id, data.storage_destination_id],
        queryFn: () => materialGroupService.options(data.storage_id, data.storage_destination_id),
        enabled: !!data.storage_id && !!data.storage_destination_id
    });

    useEffect(() => {
        const groupSelected = groups && groups.length === 1 ? groups[0].value : ""
        setData(prev => ({...prev, group: groupSelected}))
    }, [groups]);

    const {data: requesters, isLoading: isLoadingRequester} = useQuery({
        queryKey: ['requester', data.storage_destination_id],
        queryFn: () => materialStorageService.optionsUsersByStorages(data.storage_destination_id),
        enabled: !!data.storage_destination_id
    });

    useEffect(() => {
        if (isCopying) return
        setData(prev => ({...prev, requester_id: ""}));
    } , [requesters])

    const {data: materials, isLoading: isLoadingMaterials} = useQuery({
        queryKey: ['materials', data.group, data.storage_id],
        queryFn: () => materialXStorageService.optionsAvailableByGroupAndStorage(data.group, data.storage_id),
        enabled: !!data.group && !!data.storage_id
    });

    useEffect(() => {
        if (isCopying) return
        setData(prev => ({...prev, material_x_storage_id: ""}))
    }, [materials]);

    const {data: material, isLoading: isLoadingMaterial} = useQuery({
        queryKey: ['materials', data.material_x_storage_id],
        queryFn: async () => {
            const {material_id} = materials.find(m => m.value === data.material_x_storage_id)
            return await materialService.getById(material_id)
        },
        enabled: !!data.material_x_storage_id
    });

    useEffect(() => {
        let quantity = ""
        let isUnique = false
        if(material && material.is_unique) {
            quantity = 1
            isUnique = true
        }

        setMaterialIsUnique(isUnique)

        if (isCopying) return
        setData(prev => ({...prev, quantity}))
    }, [material]);

    const saved = (data) => {
        projectUtils.success(!id);

        let urlDev

        if (saveAdd) {
            urlDev = `/materials/transfers/form`;
        } else if(saveCopy){
            urlDev = `/materials/transfers/form/?cp=${data.id}`;
        } else {
            urlDev = `/materials/transfers`;
        }

        setData(initialData)
        setRequesterName("")
        setPassword("")
        setMaterialIsUnique(false)

        navigate(urlDev);
    }

    const saveBefore = () => {
        setIsLoading(true);

        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        formData.append("entirely_responsible", false);

        materialTransferService.saveBefore(formData, id)
            .then(({data}) => {
                if (data.id) {
                    saved(data)
                    return
                }

                setModalRequester(true)
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => {
                setIsLoading(false)
                saveCopy = false
                saveAdd = false
            })
    }

    const save = (assumingResponsibility = false) => {
        setIsLoading(true);

        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        if (assumingResponsibility === true) {
            formData.append("entirely_responsible", true);
        } else {
            formData.append("password", password);
        }

        materialTransferService.save(formData, id)
            .then(({data}) => {
                saved(data)
            })
            .catch((error) => {
                console.error(error);
                let message = "Não foi possível salvar.";
                if (error.response?.status !== 500) message = error.response?.data?.message;

                projectUtils.swalErrorMessage("", message, "Ok");
            })
            .finally(() => setIsLoading(false));
    };

    const saveAssuming = () => {
        Swal.fire({
            title: "Atenção",
            text: "Ao concluir sem a senha do solicitante, o almoxarife assume a responsabilidade pela movimentação do material.",
            icon: "warning",
            cancelButtonText: "Cancelar",
            cancelButtonColor: "#4a5565",
            confirmButtonText: "Continuar",
            confirmButtonColor: "#063A6C",
            showCancelButton: true,
            showConfirmButton: true,
            allowOutsideClick: true,
            reverseButtons: true
        }).then(({isConfirmed}) => {
                if (!isConfirmed) return;
                save(true);
            }
        );
    };

    return (<Template isLoading={isLoading || isLoadingData || isLoadingGroups || isLoadingRequester || isLoadingMaterials || isLoadingMaterial}>
        <Template.Header>
            <Template.Title>
                Transferências entre depósitos
            </Template.Title>
        </Template.Header>
        <Template.Body>
            <Card>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-4">
                            <SelectComponent
                                id="storage_id"
                                name="storage_id"
                                value={data.storage_id}
                                options={storageByUserOptions || []}
                                label={"Depósito de origem *"}
                                onChange={(selected) => onChangeSelect("storage_id", selected?.value)}
                            />
                        </div>
                        <div className="col-span-4">
                            <SelectComponent
                                id="storage_destination_id"
                                name="storage_destination_id"
                                value={data.storage_destination_id}
                                options={storageOptions || []}
                                label={"Depósito de destino *"}
                                onChange={(selected) => onChangeSelect("storage_destination_id", selected?.value)}
                            />
                        </div>
                        <div className="col-span-4">
                            <SelectComponent
                                id="requester_id"
                                name="requester_id"
                                value={data.requester_id}
                                options={requesters || []}
                                label={"Almoxarife solicitante *"}
                                onChange={(selected) => {
                                    onChangeSelect("requester_id", selected?.value)
                                    setRequesterName(selected?.label)
                                }}
                            />
                        </div>
                        <div className="col-span-4">
                            <SelectComponent
                                id="group"
                                name="group"
                                value={data.group}
                                options={groups || []}
                                label={"Grupo *"}
                                onChange={(selected) => onChangeSelect("group", selected?.value)}
                            />
                        </div>
                        <div className="col-span-4">
                            <SelectComponent
                                id="material_x_storage_id"
                                name="material_x_storage_id"
                                value={data.material_x_storage_id}
                                options={materials || []}
                                label={"Material *"}
                                onChange={(selected) => onChangeSelect("material_x_storage_id", selected?.value)}
                            />
                        </div>
                        <div className="col-span-2">
                            <InputForm
                                disabled={materialIsUnique}
                                type={"number"}
                                id="quantity"
                                value={data.quantity}
                                label="Quantidade *"
                                onChange={onChange}/>
                        </div>
                        <div className="col-span-2">
                            <Attach
                                attachedButtonDisabled={false}
                                showRemove={true}
                                onChangeFile={(file) => onChangeFile(file, "transfer_attach")}
                                removeFile={() => removeFiel("transfer_attach")}
                                itemId="transfer_attach"
                                attachId={data.transfer_attach}
                                attachedButtonName="Anexo"
                                modalTitle="Anexo"
                            />
                        </div>
                    </div>
                </Card.Body>

            </Card>

            <Card className={"mt-3"}>
                <Card.Body>
                    <div className="flex justify-end gap-2 w-full">
                        <Button target={"primary-outline"} onClick={() => {

                            saveCopy = true
                            saveBefore()
                        }} actionFeedbackType='save'>Salvar e copiar</Button>
                        <Button target={"primary-outline"} onClick={() => {

                            saveAdd = true
                            saveBefore()
                        }} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={saveBefore} actionFeedbackType='save'>Salvar</Button>
                    </div>
                </Card.Body>
            </Card>

            <ModalRequester
                save={save}
                saveAssuming={saveAssuming}
                setOpen={setModalRequester}
                open={modalRequester}
                data={data}
                requester={requesterName}
                password={password}
                setPassword={setPassword}
            />
        </Template.Body>
    </Template>);
};

export default MaterialTransferForm;